import { useMemo, useRef, useState } from "react";
import { Upload, Download, FileSpreadsheet, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DEFAULT_IMG = "https://images.unsplash.com/photo-1550831107-1553da8c8464?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";
const HEADERS = ["title", "specialty", "city", "state", "format", "duration", "fee", "application_fee", "seats", "stipend", "eligibility", "tags", "description", "start_date", "application_deadline", "image_url"];
const TEMPLATE = `title,specialty,city,state,format,duration,fee,application_fee,seats,stipend,eligibility,tags,description,start_date,application_deadline,image_url
Fellowship in Emergency Medicine,Emergency Medicine,Ahmedabad,Gujarat,Offline,12 months,250000,1000,15,₹35000/month,MBBS|MD,Trauma|Critical Care,Intensive acute-care fellowship,2026-08-01,2026-07-15,
Certificate in Dialysis Technology,Nephrology,Surat,Gujarat,Hybrid,6 months,55000,500,25,,B.Sc|Diploma,Dialysis|Technician,Hands-on dialysis technician program,2026-07-20,2026-07-10,`;

function parseCSV(text) {
  const rows = [];
  let field = "", row = [], inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQ = false; }
      else field += ch;
    } else if (ch === '"') inQ = true;
    else if (ch === ",") { row.push(field); field = ""; }
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(field); field = "";
      if (row.some((c) => c !== "")) rows.push(row);
      row = [];
    } else field += ch;
  }
  if (field !== "" || row.length) { row.push(field); if (row.some((c) => c !== "")) rows.push(row); }
  return rows;
}

const splitList = (s) => (s ? s.split(/[|;]/).map((x) => x.trim()).filter(Boolean) : []);
const normFormat = (f) => { const v = (f || "").trim().toLowerCase(); return v === "hybrid" ? "Hybrid" : v === "online" ? "Online" : "Offline"; };

export function BulkImportDialog({ open, onOpenChange, institutes, onDone }) {
  const [instituteId, setInstituteId] = useState("");
  const [text, setText] = useState("");
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const parsed = useMemo(() => {
    if (!text.trim()) return [];
    const rows = parseCSV(text);
    if (rows.length < 2) return [];
    const H = rows[0].map((h) => h.trim().toLowerCase());
    const get = (row, name) => { const idx = H.indexOf(name); return idx >= 0 ? (row[idx] || "").trim() : ""; };
    return rows.slice(1).map((row) => {
      const c = {
        title: get(row, "title"), specialty: get(row, "specialty"), city: get(row, "city"), state: get(row, "state"),
        format: normFormat(get(row, "format")), duration: get(row, "duration"),
        fee: parseFloat(get(row, "fee")) || 0, application_fee: parseFloat(get(row, "application_fee")) || 500,
        seats: parseInt(get(row, "seats")) || 20, stipend: get(row, "stipend"),
        eligibility: splitList(get(row, "eligibility")), tags: splitList(get(row, "tags")),
        description: get(row, "description"), start_date: get(row, "start_date") || null,
        application_deadline: get(row, "application_deadline") || null, image_url: get(row, "image_url") || DEFAULT_IMG,
        curriculum: [], documents_required: [],
        admission_process: ["Submit online application", "Document verification", "Interview", "Admission & payment"],
      };
      c._valid = Boolean(c.title && c.specialty && c.city && c.state && c.duration);
      return c;
    });
  }, [text]);

  const validCount = parsed.filter((c) => c._valid).length;
  const invalidCount = parsed.length - validCount;

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ""));
    reader.readAsText(f);
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "medlane-courses-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const doImport = async () => {
    if (!instituteId) { toast.error("Select an institute first"); return; }
    const courses = parsed.filter((c) => c._valid).map(({ _valid, ...rest }) => rest);
    if (!courses.length) { toast.error("No valid rows to import"); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/admin/courses/bulk", { institute_id: instituteId, courses });
      toast.success(`Imported ${data.created} course${data.created !== 1 ? "s" : ""}${data.failed ? ` · ${data.failed} failed` : ""}`);
      setText(""); setInstituteId("");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally { setImporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-3xl" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle>Bulk import courses</DialogTitle>
          <DialogDescription>Upload or paste a CSV of courses for one institute. Use <code>|</code> to separate multiple eligibility/tag values.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Institute</Label>
              <Select value={instituteId} onValueChange={setInstituteId}>
                <SelectTrigger data-testid="bulk-institute"><SelectValue placeholder="Select institute" /></SelectTrigger>
                <SelectContent>{institutes.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button type="button" variant="outline" className="flex-1" data-testid="bulk-upload-btn" onClick={() => fileRef.current?.click()}>
                <Upload className="mr-2 h-4 w-4" /> Upload CSV
              </Button>
              <Button type="button" variant="ghost" data-testid="bulk-template-btn" onClick={downloadTemplate}>
                <Download className="mr-2 h-4 w-4" /> Template
              </Button>
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" data-testid="bulk-file-input" onChange={onFile} />
            </div>
          </div>

          <div>
            <Label>CSV content</Label>
            <Textarea data-testid="bulk-csv-text" value={text} onChange={(e) => setText(e.target.value)} rows={8}
              className="font-mono text-xs" placeholder={`${HEADERS.join(",")}\n...`} />
          </div>

          {parsed.length > 0 && (
            <div className="rounded-xl border border-border">
              <div className="flex flex-wrap items-center gap-3 border-b border-border bg-secondary/40 px-4 py-2 text-sm">
                <span className="flex items-center gap-1 font-semibold text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="h-4 w-4" /> {validCount} ready</span>
                {invalidCount > 0 && <span className="flex items-center gap-1 font-semibold text-amber-600 dark:text-amber-400"><AlertTriangle className="h-4 w-4" /> {invalidCount} missing required fields</span>}
              </div>
              <div className="max-h-56 overflow-y-auto">
                <table className="w-full text-left text-xs">
                  <thead className="sticky top-0 bg-card text-muted-foreground">
                    <tr><th className="p-2">#</th><th className="p-2">Title</th><th className="p-2">Specialty</th><th className="p-2">City</th><th className="p-2">Format</th><th className="p-2">Fee</th></tr>
                  </thead>
                  <tbody>
                    {parsed.map((c, i) => (
                      <tr key={i} data-testid={`bulk-row-${i}`} className={c._valid ? "" : "bg-amber-50 dark:bg-amber-950/30"}>
                        <td className="p-2 text-muted-foreground">{i + 1}</td>
                        <td className="p-2 font-medium">{c.title || <span className="text-amber-600">— missing —</span>}</td>
                        <td className="p-2">{c.specialty || "—"}</td>
                        <td className="p-2">{c.city || "—"}</td>
                        <td className="p-2">{c.format}</td>
                        <td className="p-2 font-mono-nums">{inr(c.fee)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="bulk-import-btn" onClick={doImport} disabled={importing || validCount === 0 || !instituteId}>
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileSpreadsheet className="mr-2 h-4 w-4" /> Import {validCount || ""} course{validCount !== 1 ? "s" : ""}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
